// src/utils/vorlesen.js
//
// Fragen vorlesen ueber die Sprachausgabe des Browsers (Web Speech API,
// `speechSynthesis`). Bewusst kein Cloud-Dienst: kostenlos, ohne Schluessel,
// die Stimme laeuft auf dem Geraet. Android/Chrome und iPad bringen deutsche
// Stimmen mit; fuer Betrieb ohne Netz das deutsche Sprachpaket vorher in den
// Geraeteeinstellungen herunterladen.
//
// Stimme und Sprechtempo sind GERAETE-Einstellungen (localStorage): jede
// Stimme spricht von Haus aus unterschiedlich schnell, und welche Stimmen es
// gibt, haengt vom Geraet ab.
//
// Alles hier ist "best effort": fehlt die API oder eine deutsche Stimme,
// passiert einfach nichts – das Quiz laeuft normal weiter. Fuer die Fehlersuche
// meldet `vorlesenTesten()` zurueck, ob tatsaechlich gesprochen wurde.

const BUCHSTABEN = ["A", "B", "C", "D"];

export const TEMPI = [
  { wert: 0.7, label: "Sehr langsam" },
  { wert: 0.85, label: "Langsam" },
  { wert: 1, label: "Normal" },
  { wert: 1.15, label: "Schnell" },
  { wert: 1.3, label: "Sehr schnell" },
];

// BEHOBENER FEHLER: Chrome verschluckt ein speak(), das unmittelbar auf
// cancel() folgt – es passierte schlicht nichts. Deshalb kurz warten.
const NACH_ABBRUCH_MS = 80;

// BEHOBENER FEHLER: "Die erste Silbe wird verschluckt." Lautsprecher – vor
// allem Bluetooth-Boxen, aber auch manche Windows-Treiber – gehen in Pausen in
// den Stromsparmodus und brauchen beim Aufwachen einige hundert Millisekunden.
// Deshalb haelt ein unhoerbar leises Signal die Tonausgabe wach, solange
// vorgelesen wird, und nach dem Aufwecken gibt es eine Vorlaufzeit.
const AUFWACHEN_MS = 350;
const WACH_NACHLAUF_MS = 30000;

const SPEICHER = "quiz_vorlesen_geraet";

const synth = () => (typeof window !== "undefined" && window.speechSynthesis) || null;

export const vorlesenVerfuegbar = () => !!synth() && typeof window.SpeechSynthesisUtterance === "function";

// ---------------------------------------------------------------------------
// Geraete-Einstellungen

/** { stimme: voiceURI | null, stumm: boolean, tempo: { erwachsene, kinder } } */
export function geraeteEinstellungen() {
  let roh = null;
  try {
    roh = JSON.parse(localStorage.getItem(SPEICHER) || "null");
  } catch {
    roh = null;
  }
  return {
    stimme: typeof roh?.stimme === "string" ? roh.stimme : null,
    stumm: roh?.stumm === true,
    tempo: { erwachsene: tempoWert(roh?.tempo?.erwachsene), kinder: tempoWert(roh?.tempo?.kinder) },
  };
}

export function speichereGeraeteEinstellungen(e) {
  try {
    localStorage.setItem(SPEICHER, JSON.stringify(e));
  } catch {
    // Speicher gesperrt: gilt dann nur bis zum Neuladen.
  }
}

/** Tempo fuer eine Team-Kategorie ("Kinder" | sonst Erwachsene). */
export const tempoFuer = (kategorie) =>
  geraeteEinstellungen().tempo[kategorie === "Kinder" ? "kinder" : "erwachsene"];

/** Tempo aus Einstellung/Eingabe – nur sinnvolle Werte, sonst normal. */
export function tempoWert(roh) {
  const n = Number(roh);
  return Number.isFinite(n) && n >= 0.5 && n <= 2 ? n : 1;
}

/** Naechste Stufe langsamer (-1) oder schneller (+1). */
export function tempoSchritt(aktuell, richtung) {
  const i = TEMPI.findIndex((t) => t.wert === tempoWert(aktuell));
  const j = Math.min(TEMPI.length - 1, Math.max(0, (i < 0 ? 2 : i) + richtung));
  return TEMPI[j].wert;
}

export const tempoLabel = (wert) => TEMPI.find((t) => t.wert === tempoWert(wert))?.label || "Normal";

// ---------------------------------------------------------------------------
// Stimmen

/** Alle deutschen Stimmen des Geraets, beste zuerst. */
export function deutscheStimmen() {
  const s = synth();
  if (!s) return [];
  const punkte = (v) =>
    (/natural|neural|premium|enhanced|google/i.test(v.name) ? 3 : 0) +
    (v.localService ? 1 : 0) +
    (/de-DE/i.test(v.lang) ? 1 : 0);
  return s
    .getVoices()
    .filter((v) => /^de(-|_|$)/i.test(v.lang))
    .sort((a, b) => punkte(b) - punkte(a));
}

/** Gewaehlte Stimme, sonst die beste vorhandene, sonst null (Browser waehlt). */
function stimme() {
  const alle = deutscheStimmen();
  if (alle.length === 0) return null;
  const gewaehlt = geraeteEinstellungen().stimme;
  return alle.find((v) => v.voiceURI === gewaehlt) || alle[0];
}

export const stimmenName = () => stimme()?.name || null;

/** Rueckruf, sobald der Browser seine Stimmenliste (nach)geladen hat. */
export function beiStimmenGeladen(fn) {
  const s = synth();
  if (!s || !s.addEventListener) return () => {};
  s.addEventListener("voiceschanged", fn);
  return () => s.removeEventListener("voiceschanged", fn);
}

// Stimmenliste vorladen (Chrome liefert sie erst nach `voiceschanged`).
if (vorlesenVerfuegbar()) {
  try {
    synth().getVoices();
  } catch {
    // egal
  }
}

// ---------------------------------------------------------------------------
// Tonausgabe wach halten

let audioCtx = null;
let wachQuelle = null;
let wachTimer = null;

/** Startet das unhoerbare Wachhalte-Signal; true, wenn es gerade erst anlief. */
function wachHalten() {
  clearTimeout(wachTimer);
  wachTimer = setTimeout(wachSchlafen, WACH_NACHLAUF_MS);
  if (wachQuelle) return false;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return false;
    if (!audioCtx) audioCtx = new Ctor();
    if (audioCtx.state === "suspended") audioCtx.resume().catch(() => {});
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    // Tiefer Ton bei verschwindender Lautstaerke: fuer niemanden hoerbar, fuer
    // den Lautsprecher aber "es laeuft Ton".
    osc.frequency.value = 30;
    gain.gain.value = 0.0005;
    osc.connect(gain).connect(audioCtx.destination);
    osc.start();
    wachQuelle = osc;
    return true;
  } catch {
    return false;
  }
}

function wachSchlafen() {
  clearTimeout(wachTimer);
  wachTimer = null;
  if (wachQuelle) {
    try {
      wachQuelle.stop();
    } catch {
      // egal
    }
    wachQuelle = null;
  }
}

// ---------------------------------------------------------------------------
// Vorlesen

/** Der vorzulesende Text einer Frage – bei Mehrfachauswahl mit Antworten. */
export function vorlesetext(frage) {
  if (!frage) return "";
  const text = String(frage.text || "").trim();
  if (frage.typ !== "mc") return text;
  const antworten = (frage.antworten || []).map((a, i) => `${BUCHSTABEN[i]}: ${a}.`).join(" ");
  return `${text} ${antworten}`;
}

let startTimer = null;
// Referenz halten: Chrome raeumt sonst die Aeusserung mitten im Sprechen ab
// (die Stimme verstummt dann ohne Ereignis).
let aktuelle = null;

/** Laufendes Vorlesen sofort beenden. */
export function vorlesenStoppen() {
  clearTimeout(startTimer);
  startTimer = null;
  aktuelle = null;
  const s = synth();
  if (s) {
    try {
      s.cancel();
    } catch {
      // egal
    }
  }
}

/** Alles beenden, auch das Wachhalten (beim Verlassen der Seite). */
export function vorlesenAus() {
  vorlesenStoppen();
  wachSchlafen();
}

/**
 * Text vorlesen; bricht vorher Laufendes ab.
 * `beiStart` / `beiFehler(code)` nur fuer die Testanzeige.
 */
export function vorlesen(text, { tempo = 1, beiStart, beiFehler } = {}) {
  const s = synth();
  if (!s || !text || !vorlesenVerfuegbar()) {
    if (beiFehler) beiFehler("nicht-verfuegbar");
    return;
  }
  try {
    clearTimeout(startTimer);
    const lief = s.speaking || s.pending;
    if (lief) s.cancel();
    const geradeAufgeweckt = wachHalten();
    const u = new window.SpeechSynthesisUtterance(text);
    u.lang = "de-DE";
    const v = stimme();
    if (v) u.voice = v;
    u.rate = tempoWert(tempo);
    u.onstart = () => beiStart && beiStart();
    u.onend = () => wachHalten(); // Nachlauf ab Satzende
    u.onerror = (e) => {
      // "interrupted"/"canceled" = wir haben selbst abgebrochen (naechste Frage).
      if (beiFehler && e && e.error !== "interrupted" && e.error !== "canceled") beiFehler(e.error || "fehler");
    };
    aktuelle = u;
    const los = () => {
      startTimer = null;
      if (aktuelle !== u) return;
      // Haengt die Ausgabe (Chrome nach Tab-Wechsel), loest resume() sie.
      if (s.paused) s.resume();
      s.speak(u);
    };
    const warten = geradeAufgeweckt ? AUFWACHEN_MS : lief ? NACH_ABBRUCH_MS : 0;
    if (warten) startTimer = setTimeout(los, warten);
    else los();
  } catch (err) {
    // Sprachausgabe ist nur eine Hilfe – nie das Quiz stoeren.
    if (beiFehler) beiFehler(err?.message || "fehler");
  }
}

/**
 * Im Klick-Handler aufrufen (z. B. "Los geht's"): iPad/Safari erlaubt
 * Sprachausgabe (und Web Audio) erst nach einer Beruehrung. Weckt dabei auch
 * gleich den Lautsprecher.
 */
export function vorlesenFreischalten() {
  const s = synth();
  if (!s || !vorlesenVerfuegbar()) return;
  wachHalten();
  try {
    if (s.speaking || s.pending) return;
    const u = new window.SpeechSynthesisUtterance(" ");
    u.volume = 0;
    s.speak(u);
  } catch {
    // egal
  }
}

const FEHLERTEXTE = {
  "nicht-verfuegbar": "Dieser Browser hat keine Sprachausgabe.",
  "not-allowed": "Der Browser hat die Sprachausgabe blockiert – einmal auf die Seite tippen und erneut testen.",
  "synthesis-unavailable": "Keine Stimme verfügbar – deutsches Sprachpaket in den Geräteeinstellungen installieren.",
  "language-unavailable": "Keine deutsche Stimme – deutsches Sprachpaket in den Geräteeinstellungen installieren.",
  "voice-unavailable": "Die Stimme ist nicht verfügbar – deutsches Sprachpaket installieren.",
  "audio-busy": "Der Lautsprecher ist gerade belegt.",
  network: "Die gewählte Stimme braucht Internet – Verbindung prüfen oder eine Stimme ohne „online“ wählen.",
};

/**
 * Probesatz sprechen und Ergebnis melden: `meldung({ ok, text })`.
 * Kommt nach 4 s kein Start, ist die Ausgabe vermutlich stumm/blockiert.
 */
export function vorlesenTesten(meldung, { tempo = 1, text } = {}) {
  let erledigt = false;
  const fertig = (ok, t) => {
    if (erledigt) return;
    erledigt = true;
    clearTimeout(timer);
    meldung({ ok, text: t });
  };
  const timer = setTimeout(
    () => fertig(false, "Keine Ausgabe gestartet. Lautstärke, Stummschaltung und deutsches Sprachpaket prüfen."),
    4000
  );
  vorlesen(text || "Das ist ein Test. Ein Oktopus hat drei Herzen.", {
    tempo,
    beiStart: () => fertig(true, `Spricht mit Stimme: ${stimmenName() || "Standardstimme des Geräts"}`),
    beiFehler: (code) => fertig(false, FEHLERTEXTE[code] || `Sprachausgabe fehlgeschlagen (${code}).`),
  });
}
